/**
 * Button — the single button used everywhere in the app.
 *
 * WHY ONE COMPONENT INSTEAD OF STYLING <button> EACH TIME
 * Every raw <button> in a codebase ends up slightly different — different
 * padding, different green, different hover. One component means every button
 * in Rocket07 looks and behaves identically, and a design tweak is one edit
 * here rather than a hunt across the app.
 *
 * `variant` chooses the look; `size` chooses the scale. Both are just lookups
 * into the maps below, so adding a new style is one line.
 */

import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

const VARIANTS = {
  // Primary action — "Add to cart", "Place order". One per screen, ideally.
  // Golden-yellow fill with DARK ink text so it stays readable (like the app).
  primary: 'bg-primary-500 text-ink hover:bg-primary-600 active:bg-primary-700 shadow-sm',
  // Secondary — outlined, for less important actions next to a primary.
  secondary: 'bg-white text-ink border border-line hover:bg-canvas active:bg-line',
  // Subtle — no border, for tertiary actions and toolbars.
  ghost: 'bg-transparent text-ink hover:bg-line/60 active:bg-line',
  // Destructive — delete, remove, cancel order.
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90 shadow-sm',
  // Green text link that still has a button's tap target.
  link: 'bg-transparent text-primary-700 hover:text-primary-800 hover:underline px-0',
};

const SIZES = {
  sm: 'h-9 px-3.5 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-13 px-7 text-base gap-2.5',
  icon: 'h-11 w-11 p-0', // square, for an icon on its own
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className,
  type = 'button',
  ...props
}) {
  // While loading, the button must also be non-interactive, so a double-tap
  // cannot fire the action twice (a classic double-order bug).
  const isDisabled = disabled || isLoading;

  return (
    <button
      // Defaulting type to "button" prevents the #1 React form bug: a button
      // inside a <form> defaults to type="submit" and reloads the page.
      type={type}
      disabled={isDisabled}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-semibold',
        'transition-all duration-150 select-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <>
          <Spinner size="sm" className={variant === 'danger' ? 'text-white' : ''} />
          <span>Please wait…</span>
        </>
      ) : (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      )}
    </button>
  );
}

export default Button;
